import type { JobClickTrackingSelectedJobAttributes } from "../../../../context/ViewJobDataContext";

import {
  createRawClickLoggingUrl,
  emitRawClickSignal,
  type RawClickAttrs,
  type RawClickOptions,
} from "./rawClickTracking";
import { isJobFromHomePageWeb, isJobFromSerpWeb } from "../../../../utils/fromParamUtils";

export type { RawClickAttrs, RawClickOptions } from "./rawClickTracking";

/**
 * Web implementation of legacy auto-open job-click tracking, ported from
 * jobsearch-ui `jasxJobClick.tsx` (`triggerJobClickTracking` /
 * `triggerRawClickLogging`). Fired from the RNVJ ApplyButton when the viewjob was
 * auto-opened in a desktop two-pane feed (the user never clicked the job card),
 * so the job click is counted at apply time.
 *
 * Native keeps the auto-open beacons as no-ops but implements authenticated
 * raw-click transport (see `jobClickTracking.ts`).
 */

/**
 * Resolve the rawclick `surface` to one of: 'viewjob', 'homepage', 'serp'.
 *
 * Full-screen standalone viewjob (mobile/desktop) is always 'viewjob'. A host
 * bottom sheet or embedded VJ pane is attributed to its host page via the `from`
 * URL param, which the host already sets on the viewjob link (e.g. `from=hp` /
 * `mobhp_*` for homepage, `from=serp` / `tp-serp` for SERP). `from` is read from
 * the VJ context (see `useViewJobDataContext().from`).
 *
 * Mobile bottom sheets also report `MOBILE_STANDALONE`, so `viewJobDisplay`
 * alone cannot distinguish them from the full-screen mobile ViewJob page.
 */
export function resolveRawClickSurface(
  from: string | undefined | null,
  viewJobDisplay: string,
  isBottomSheet: boolean,
): string {
  if (
    viewJobDisplay === "DESKTOP_STANDALONE" ||
    (viewJobDisplay === "MOBILE_STANDALONE" && !isBottomSheet)
  ) {
    return "viewjob";
  }
  if (isJobFromHomePageWeb(from)) {
    return "homepage";
  }
  if (isJobFromSerpWeb(from)) {
    return "serp";
  }
  return "viewjob";
}

function createImgBeacon(url: string): void {
  const imgBeacon = new globalThis.Image();
  imgBeacon.src = url;
}

/**
 * Pushes a history entry updating `vjk` to reflect the selected job while
 * removing tracking parameters that must not remain in browser history.
 */
function pushSelectedJobHistory(
  selectedJobAttributes: JobClickTrackingSelectedJobAttributes,
): void {
  try {
    const currentUrl = new URL(window.location.href);
    const params = currentUrl.searchParams;
    params.delete("advn");
    if (selectedJobAttributes.jobKey) {
      params.set("vjk", selectedJobAttributes.jobKey);
    }
    params.delete("indconvtk");
    window.history.pushState(null, "", currentUrl.href);
  } catch (error) {
    console.error("[jobClickTracking] Failed to push selected job history:", error);
  }
}

/**
 * Builds the internal click-tracking URL: adds `vjs=2`, `autoopen=1`, and a
 * cache-busting `__` param. Mirrors legacy `getInternalTrackingURL`. This util
 * is only invoked for auto-opened jobs, so `autoopen=1` is always set.
 */
function getInternalTrackingURL(internalTrackUrl: URL): string {
  internalTrackUrl.searchParams.set("vjs", "2");
  internalTrackUrl.searchParams.set("autoopen", "1");
  internalTrackUrl.searchParams.append("__", Math.random().toString());

  return internalTrackUrl.href;
}

function triggerExternalTracking(trackingUrls: string[] | null | undefined): void {
  if (!trackingUrls) {
    return;
  }
  trackingUrls.forEach((url: string) => {
    try {
      const externalTrackingUrl = new URL(url);
      externalTrackingUrl.searchParams.append("__", Math.random().toString());
      createImgBeacon(externalTrackingUrl.href);
    } catch (error) {
      console.error("[jobClickTracking] Failed to fire external tracking beacon:", error);
    }
  });
}

export function triggerJobClickTracking(
  selectedJobAttributes: JobClickTrackingSelectedJobAttributes | undefined,
): void {
  if (!selectedJobAttributes || !selectedJobAttributes.link) {
    return;
  }
  pushSelectedJobHistory(selectedJobAttributes);
  try {
    const baseInternalTrackingUrl = new URL(selectedJobAttributes.link, window.location.origin);
    createImgBeacon(getInternalTrackingURL(baseInternalTrackingUrl));
  } catch (error) {
    console.error("[jobClickTracking] Failed to fire internal tracking beacon:", error);
  }
  triggerExternalTracking(selectedJobAttributes.extractTrackingUrls);
}

export function triggerRawClickLogging(
  attrs: RawClickAttrs | undefined,
  isLoggedIn: boolean,
  options: RawClickOptions = {},
  _baseUrl?: string,
): void {
  if (!attrs) {
    return;
  }
  emitRawClickSignal(attrs, isLoggedIn, {
    ...options,
    dgToken:
      window._initialData?.deploymentGroupToken ??
      window._initialData?.viewJobClientSideModel?.dgToken ??
      options.dgToken,
    isCanary: window._initialData?.deploymentGroup?.toLowerCase()?.includes("canary") ?? false,
  });
  try {
    const rawClickLoggingUrl = createRawClickLoggingUrl(
      window.location.origin,
      attrs,
      isLoggedIn,
      options,
    );
    void fetch(rawClickLoggingUrl, { keepalive: true }).catch(() => {});
  } catch {
    // Raw-click logging is best-effort and must not block navigation.
  }
}
